import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, BarChart3, Tag, Download, ChevronRight, Shield, TrendingUp,
  MessageSquare, FileText, Trash2, RotateCcw, Eye, Plus, X, Search,
  CheckCircle, Clock, UserPlus, Percent, ToggleLeft, ToggleRight, BookOpen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-[#141414] border border-[#262626] rounded-sm p-5 hover:border-[#FF3B30]/30 transition-all">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent || 'bg-[#FF3B30]/10'}`}>
          <Icon className={`w-4 h-4 ${accent ? 'text-white' : 'text-[#FF3B30]'}`} />
        </div>
        <span className="text-[#A3A3A3] text-xs tracking-[0.15em] uppercase font-bold">{label}</span>
      </div>
      <p className="text-white font-bold text-2xl">{value}</p>
      {sub && <p className="text-[#737373] text-xs mt-1">{sub}</p>}
    </div>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [coachHistory, setCoachHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Create user modal
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: 'RiskCoach2026!', role: 'user' });
  
  // Create code modal
  const [showCreateCode, setShowCreateCode] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', discount_percent: 20, max_uses: null, description: '' });

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/discount-codes`, { withCredentials: true });
      setCodes(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchUsers(), fetchCodes()]);
      setLoading(false);
    };
    load();
  }, [fetchStats, fetchUsers, fetchCodes]);

  const viewUser = async (userId) => {
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
      setSelectedUser(data);
    } catch (e) { console.error(e); }
  };

  const viewCoachHistory = async (userId) => {
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/coach-history`, { withCredentials: true });
      setCoachHistory(data);
    } catch (e) { console.error(e); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u._id !== userId));
      setSelectedUser(null);
      fetchStats();
    } catch (e) { console.error(e); }
  };

  const resetPassword = async (userId) => {
    try {
      const { data } = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { withCredentials: true });
      alert(data.message);
    } catch (e) { console.error(e); }
  };

  const resetAssessment = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-assessment`, {}, { withCredentials: true });
      alert('Assessment reset');
      viewUser(userId);
    } catch (e) { console.error(e); }
  };

  const updateUserWeek = async (userId, week) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { current_week: week }, { withCredentials: true });
      fetchUsers();
      if (selectedUser && selectedUser._id === userId) viewUser(userId);
    } catch (e) { console.error(e); }
  };

  const createUser = async () => {
    try {
      await axios.post(`${API}/admin/users`, newUser, { withCredentials: true });
      setShowCreateUser(false);
      setNewUser({ name: '', email: '', password: 'RiskCoach2026!', role: 'user' });
      fetchUsers();
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'Error creating user'); }
  };

  const createCode = async () => {
    try {
      await axios.post(`${API}/admin/discount-codes`, newCode, { withCredentials: true });
      setShowCreateCode(false);
      setNewCode({ code: '', discount_percent: 20, max_uses: null, description: '' });
      fetchCodes();
    } catch (e) { alert(e.response?.data?.detail || 'Error creating code'); }
  };

  const toggleCode = async (code) => {
    try {
      await axios.put(`${API}/admin/discount-codes/${code}`, {}, { withCredentials: true });
      fetchCodes();
    } catch (e) { console.error(e); }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete discount code ${code}?`)) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${code}`, { withCredentials: true });
      fetchCodes();
    } catch (e) { console.error(e); }
  };

  const exportUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/export/users`, { withCredentials: true });
      const csv = [Object.keys(data[0] || {}).join(','), ...data.map(r => Object.values(r).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'riskcoach_users.csv'; a.click();
    } catch (e) { console.error(e); }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.organization?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', icon: BarChart3, label: 'Overview' },
    { id: 'users', icon: Users, label: 'Users' },
    { id: 'codes', icon: Tag, label: 'Discount Codes' },
  ];

  return (
    <div data-testid="admin-page" className="p-6 md:p-8 lg:p-12 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-2">Admin</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Command Center</h1>
        </div>
        <button
          data-testid="export-users-button"
          onClick={exportUsers}
          className="flex items-center gap-2 bg-[#141414] border border-[#262626] hover:border-[#FF3B30]/50 text-[#A3A3A3] hover:text-white px-4 py-2 rounded-sm transition-all text-sm font-medium"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 bg-[#141414] border border-[#262626] rounded-sm p-1 w-fit">
        {tabs.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => { setTab(id); setSelectedUser(null); setCoachHistory(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold transition-all
              ${tab === id ? 'bg-[#FF3B30] text-white' : 'text-[#A3A3A3] hover:text-white'}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {tab === 'overview' && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total Users" value={stats.total_users} sub={`${stats.recent_signups_7d} joined this week`} />
            <StatCard icon={TrendingUp} label="Active Users" value={stats.active_users} sub="progressed past Week 1" />
            <StatCard icon={CheckCircle} label="Assessments Done" value={stats.assessments_completed} sub={`${stats.total_users > 0 ? Math.round(stats.assessments_completed / stats.total_users * 100) : 0}% completion rate`} />
            <StatCard icon={MessageSquare} label="Coach Messages" value={stats.total_coach_messages} sub={`${stats.total_reflections} reflections written`} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard icon={BookOpen} label="Avg Modules Completed" value={stats.avg_modules_completed} sub="across all users" />
            <StatCard icon={Shield} label="Program Completions" value={stats.total_completions} sub="reached Week 24" />
          </div>

          {/* Phase Distribution */}
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] mb-4">User Distribution by Phase</p>
            <div className="space-y-3">
              {Object.entries(stats.phase_distribution || {}).map(([phase, count]) => (
                <div key={phase}>
                  <div className="flex justify-between mb-1">
                    <span className="text-white text-sm">{phase}</span>
                    <span className="text-[#A3A3A3] text-sm font-bold">{count}</span>
                  </div>
                  <div className="w-full h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF3B30] rounded-full transition-all" style={{ width: `${stats.total_users > 0 ? (count / stats.total_users * 100) : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Coach Personality Split */}
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] mb-4">Coach Personality Preference</p>
            <div className="flex gap-4">
              {Object.entries(stats.personality_distribution || {}).map(([p, count]) => (
                <div key={p} className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded-sm p-4 text-center">
                  <p className="text-white font-bold text-2xl">{count}</p>
                  <p className="text-[#A3A3A3] text-sm capitalize">{p}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* USERS TAB */}
      {tab === 'users' && !selectedUser && !coachHistory && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
              <input
                data-testid="user-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or organization..."
                className="w-full bg-[#141414] border border-[#262626] rounded-sm pl-10 pr-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm"
              />
            </div>
            <button
              data-testid="create-user-button"
              onClick={() => setShowCreateUser(true)}
              className="flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white px-4 py-2.5 rounded-sm transition-colors text-sm font-bold"
            >
              <UserPlus className="w-4 h-4" /> Add User
            </button>
          </div>

          <div className="bg-[#141414] border border-[#262626] rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#262626]">
                  {['Name', 'Email', 'Week', 'Progress', 'Coach Sessions', 'Role', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs tracking-[0.15em] uppercase font-bold text-[#A3A3A3]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const completed = (u.completed_modules || []).length;
                  const pct = Math.round(completed / 24 * 100);
                  return (
                    <tr key={u._id} data-testid={`user-row-${u._id}`} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors">
                      <td className="px-4 py-3 text-white text-sm font-medium">{u.name}</td>
                      <td className="px-4 py-3 text-[#A3A3A3] text-sm">{u.email}</td>
                      <td className="px-4 py-3 text-white text-sm">{u.current_week || 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                            <div className="h-full bg-[#FF3B30] rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[#A3A3A3] text-xs">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#A3A3A3] text-sm">{u.coach_messages || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-sm ${u.role === 'admin' ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-[#262626] text-[#A3A3A3]'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => viewUser(u._id)} className="p-1.5 hover:bg-[#262626] rounded-sm transition-colors text-[#A3A3A3] hover:text-white" title="View details">
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => viewCoachHistory(u._id)} className="p-1.5 hover:bg-[#262626] rounded-sm transition-colors text-[#A3A3A3] hover:text-white" title="Coach history">
                            <MessageSquare className="w-3.5 h-3.5" />
                          </button>
                          {u.role !== 'admin' && (
                            <button onClick={() => deleteUser(u._id)} className="p-1.5 hover:bg-[#262626] rounded-sm transition-colors text-[#A3A3A3] hover:text-[#EF4444]" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* USER DETAIL VIEW */}
      {tab === 'users' && selectedUser && !coachHistory && (
        <div className="space-y-6">
          <button onClick={() => setSelectedUser(null)} className="text-[#A3A3A3] hover:text-white text-sm flex items-center gap-1 mb-4">
            &larr; Back to users
          </button>
          
          <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-xl">{selectedUser.name}</h2>
              <p className="text-[#A3A3A3] text-sm">{selectedUser.email}</p>
              <p className="text-[#737373] text-xs mt-1">{selectedUser.title || 'No title'} {selectedUser.organization ? `at ${selectedUser.organization}` : ''}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => resetPassword(selectedUser._id)} data-testid="admin-reset-pw" className="text-xs bg-[#262626] hover:bg-[#333] text-white px-3 py-2 rounded-sm transition-colors flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Reset Password
              </button>
              <button onClick={() => resetAssessment(selectedUser._id)} className="text-xs bg-[#262626] hover:bg-[#333] text-white px-3 py-2 rounded-sm transition-colors flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> Reset Assessment
              </button>
              <button onClick={() => viewCoachHistory(selectedUser._id)} className="text-xs bg-[#FF3B30] hover:bg-[#D62828] text-white px-3 py-2 rounded-sm transition-colors flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Coach History
              </button>
            </div>
          </div>

          {/* Progress */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <p className="text-[#A3A3A3] text-xs tracking-[0.15em] uppercase font-bold mb-2">Current Week</p>
              <div className="flex items-center gap-3">
                <p className="text-white font-bold text-3xl">{selectedUser.current_week || 1}</p>
                <div className="flex gap-1">
                  <button onClick={() => updateUserWeek(selectedUser._id, (selectedUser.current_week || 1) - 1)} className="w-7 h-7 bg-[#262626] hover:bg-[#333] rounded-sm flex items-center justify-center text-white text-sm">-</button>
                  <button onClick={() => updateUserWeek(selectedUser._id, (selectedUser.current_week || 1) + 1)} className="w-7 h-7 bg-[#262626] hover:bg-[#333] rounded-sm flex items-center justify-center text-white text-sm">+</button>
                </div>
              </div>
            </div>
            <div className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <p className="text-[#A3A3A3] text-xs tracking-[0.15em] uppercase font-bold mb-2">Coach Messages</p>
              <p className="text-white font-bold text-3xl">{selectedUser.coach_messages || 0}</p>
            </div>
            <div className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <p className="text-[#A3A3A3] text-xs tracking-[0.15em] uppercase font-bold mb-2">Reflections</p>
              <p className="text-white font-bold text-3xl">{selectedUser.reflection_count || 0}</p>
            </div>
          </div>

          {/* Assessment */}
          {selectedUser.assessment && (
            <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] mb-4">Assessment Results — Overall: {selectedUser.assessment.overall_score}/5</p>
              <div className="space-y-3">
                {Object.entries(selectedUser.assessment.dimension_scores || {}).map(([dim, score]) => (
                  <div key={dim}>
                    <div className="flex justify-between mb-1">
                      <span className="text-white text-sm flex items-center gap-2">
                        {dim}
                        {selectedUser.assessment.strengths?.includes(dim) && <span className="text-[#22C55E] text-[10px] font-bold">STRENGTH</span>}
                        {selectedUser.assessment.growth_areas?.includes(dim) && <span className="text-[#F59E0B] text-[10px] font-bold">GROWTH</span>}
                      </span>
                      <span className="text-[#A3A3A3] text-sm">{score}/5</span>
                    </div>
                    <div className="w-full h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${selectedUser.assessment.strengths?.includes(dim) ? 'bg-[#22C55E]' : selectedUser.assessment.growth_areas?.includes(dim) ? 'bg-[#F59E0B]' : 'bg-[#FF3B30]'}`} style={{ width: `${(score / 5) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coach Notes */}
          {selectedUser.coach_notes?.length > 0 && (
            <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] mb-4">Coach Insights</p>
              <div className="space-y-2">
                {selectedUser.coach_notes.map((n, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm shrink-0 mt-0.5
                      ${n.category === 'breakthrough' ? 'bg-[#22C55E]/10 text-[#22C55E]' :
                        n.category === 'belief' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' :
                        n.category === 'commitment' ? 'bg-blue-500/10 text-blue-400' :
                        'bg-[#FF3B30]/10 text-[#FF3B30]'}`}
                    >
                      {n.category}
                    </span>
                    <p className="text-[#A3A3A3] text-sm">{n.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* COACH HISTORY VIEW */}
      {tab === 'users' && coachHistory && (
        <div className="space-y-4">
          <button onClick={() => { setCoachHistory(null); }} className="text-[#A3A3A3] hover:text-white text-sm flex items-center gap-1 mb-4">
            &larr; Back
          </button>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-2">Coach Conversation History</p>
          
          {coachHistory.length === 0 ? (
            <p className="text-[#737373] text-sm">No coaching sessions yet.</p>
          ) : (
            coachHistory.map((session, si) => (
              <div key={si} className="bg-[#141414] border border-[#262626] rounded-sm p-4 mb-4">
                <p className="text-[#A3A3A3] text-xs font-bold mb-3">Session: {session.message_count} messages</p>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {session.messages.map((msg, mi) => (
                    <div key={mi} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                      <div className={`max-w-[80%] rounded-sm p-3 text-sm
                        ${msg.role === 'user' ? 'bg-[#FF3B30]/10 text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#A3A3A3]'}`}>
                        <p className="text-[10px] text-[#737373] mb-1">{msg.role === 'user' ? 'USER' : 'COACH'}</p>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* DISCOUNT CODES TAB */}
      {tab === 'codes' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[#A3A3A3] text-sm">{codes.length} discount codes</p>
            <button
              data-testid="create-code-button"
              onClick={() => setShowCreateCode(true)}
              className="flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D62828] text-white px-4 py-2.5 rounded-sm transition-colors text-sm font-bold"
            >
              <Plus className="w-4 h-4" /> Create Code
            </button>
          </div>

          <div className="bg-[#141414] border border-[#262626] rounded-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#262626]">
                  {['Code', 'Discount', 'Uses', 'Description', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs tracking-[0.15em] uppercase font-bold text-[#A3A3A3]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} data-testid={`code-row-${c.code}`} className="border-b border-[#262626] hover:bg-[#1A1A1A] transition-colors">
                    <td className="px-4 py-3 text-white text-sm font-mono font-bold">{c.code}</td>
                    <td className="px-4 py-3 text-[#FF3B30] text-sm font-bold">{c.discount_percent}%</td>
                    <td className="px-4 py-3 text-[#A3A3A3] text-sm">{c.times_used}{c.max_uses ? `/${c.max_uses}` : ''}</td>
                    <td className="px-4 py-3 text-[#A3A3A3] text-sm">{c.description || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-sm ${c.active ? 'bg-[#22C55E]/10 text-[#22C55E]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>
                        {c.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => toggleCode(c.code)} className="p-1.5 hover:bg-[#262626] rounded-sm transition-colors text-[#A3A3A3] hover:text-white" title="Toggle status">
                          {c.active ? <ToggleRight className="w-4 h-4 text-[#22C55E]" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                        <button onClick={() => deleteCode(c.code)} className="p-1.5 hover:bg-[#262626] rounded-sm transition-colors text-[#A3A3A3] hover:text-[#EF4444]" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {codes.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-[#737373] text-sm">No discount codes yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CREATE USER MODAL */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreateUser(false)}>
          <div data-testid="create-user-modal" className="bg-[#141414] border border-[#262626] rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg">Add User</h3>
              <button onClick={() => setShowCreateUser(false)} className="text-[#737373] hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input value={newUser.name} onChange={(e) => setNewUser(p => ({ ...p, name: e.target.value }))} placeholder="Full Name" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm" />
              <input value={newUser.email} onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="Email" type="email" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm" />
              <input value={newUser.password} onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="Password" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm" />
              <select value={newUser.role} onChange={(e) => setNewUser(p => ({ ...p, role: e.target.value }))} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <button data-testid="submit-create-user" onClick={createUser} className="w-full bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold py-2.5 rounded-sm transition-colors text-sm">Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE CODE MODAL */}
      {showCreateCode && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreateCode(false)}>
          <div data-testid="create-code-modal" className="bg-[#141414] border border-[#262626] rounded-sm p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg">Create Discount Code</h3>
              <button onClick={() => setShowCreateCode(false)} className="text-[#737373] hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input value={newCode.code} onChange={(e) => setNewCode(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="Code (e.g., LAUNCH50)" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm font-mono" />
              <div>
                <label className="text-[#A3A3A3] text-xs block mb-1">Discount %</label>
                <input type="number" min="1" max="100" value={newCode.discount_percent} onChange={(e) => setNewCode(p => ({ ...p, discount_percent: parseInt(e.target.value) || 0 }))} className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm" />
              </div>
              <div>
                <label className="text-[#A3A3A3] text-xs block mb-1">Max Uses (leave empty for unlimited)</label>
                <input type="number" min="1" value={newCode.max_uses || ''} onChange={(e) => setNewCode(p => ({ ...p, max_uses: e.target.value ? parseInt(e.target.value) : null }))} placeholder="Unlimited" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm" />
              </div>
              <input value={newCode.description} onChange={(e) => setNewCode(p => ({ ...p, description: e.target.value }))} placeholder="Description (e.g., Early bird special)" className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white placeholder:text-[#737373] focus:outline-none focus:ring-1 focus:ring-[#FF3B30] text-sm" />
              <button data-testid="submit-create-code" onClick={createCode} className="w-full bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold py-2.5 rounded-sm transition-colors text-sm">Create Code</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
